import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";

interface HelpModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function HelpModal({ isOpen, onOpenChange }: HelpModalProps) {
  const { user } = useAuth();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Help & Getting Started</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          <div>
            <h3 className="font-semibold text-lg mb-2">Welcome to JamControl!</h3>
            <p className="text-sm text-slate-600 mb-4">
              JamControl helps you manage open mic or jam night events in real time, from signup to stage
              management.
            </p>
          </div>

          <div>
            <h4 className="font-medium mb-2">Use JamControl to:</h4>
            <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
              <li>Share links that allow participants to sign up themselves, or register them manually yourself</li>
              <li>Design your running order & keep track of performance times</li>
              <li>Coordinate with others on larger / more complex events and ensure everyone keeps in sync</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-2">Getting Started:</h4>
            <p className="text-sm text-slate-600 mb-4">
              If you're an owner, you can create new events in whatever organisations you're a part of. There are
              two event types: <b>Jam Night</b> and <b>Open Mic</b> which both have different workflows, but benefit
              from some common features.
            </p>
          </div>

          <div>
            <h4 className="font-medium mb-2">Event Types:</h4>
            <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
              <li><strong>Jam Nights:</strong> More complex staging workflow (signed up → selected for stage → on stage)
                including the ability to earmark people to swap for others, helping you keep control of the chaos!
              </li>
              <li><strong>Open Mic:</strong> Simple ordered list with drag-and-drop reordering to set your running
                order, and keep track of how long people have been on stage.
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-2">Stage Management Workflow:</h4>
            <div className="text-sm text-slate-600 space-y-2">
              <p><strong>Open Mic Events:</strong></p>
              <ul className="ml-4 list-disc space-y-1">
                <li><strong>Queue Management:</strong> All participants appear in a single list that you can reorder by dragging and dropping</li>
                <li><strong>Performance Flow:</strong> Click "On Stage" to move someone from the queue to active performance</li>
                <li><strong>Time Tracking:</strong> Automatic timer starts when someone goes on stage, tracks total performance time</li>
                <li><strong>Stage Control:</strong> "Off Stage" button removes performer and stops timer, moving them to completed</li>
                <li><strong>Priority System:</strong> Mark participants as priority to highlight them in the queue</li>
              </ul>
              
              <p className="mt-3"><strong>Jam Night Events:</strong></p>
              <ul className="ml-4 list-disc space-y-1">
                <li><strong>Multi-Stage Process:</strong> Participants move through: Participants → Selected for Stage → On Stage</li>
                <li><strong>Selection Strategy:</strong> Use "Select for Stage" to choose who you want to go on next</li>
                <li><strong>Replacement System:</strong> When selecting someone, optionally choose who they'll replace on stage</li>
                <li><strong>Selected for State:</strong> Selected performers appear in "Selected" section - so you can go find and get them ready!</li>
                <li><strong>Stage Transitions:</strong> "On Stage" button moves ready performers to active stage, automatically removes replacements</li>
                <li><strong>Flexible Management:</strong> Move people back to queue, skip selections, or reorganize the flow as needed</li>
              </ul>
              
              <p className="mt-3"><strong>Common Stage Features:</strong></p>
              <ul className="ml-4 list-disc space-y-1">
                <li><strong>Visual Status:</strong> Color-coded participant cards show current status at a glance</li>
                <li><strong>Performance Notes:</strong> Add notes about performances for future reference</li>
                <li><strong>Stage History:</strong> View completed performances with times and notes</li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">General Workflow:</h4>
            <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">

              <li><strong>Real Time Sync:</strong> Any other users working the same event will see every update
                in real time!
              </li>
              <li><strong>Public Signup Links:</strong> JamControl generates unique signup links for each event that you can share publicly.
                <p className="mt-2"><strong>How Signup Links Work:</strong></p>
                <ul className="ml-4 list-disc space-y-1 mt-1">
                  <li>Each event gets its own unique signup URL (e.g., jamcontrol.app/signup/abc123)</li>
                  <li>Participants fill out their name, performance details, and contact information</li>
                  <li>They can specify if they're solo, a band, what instruments etc. as well as a note. (which fields are captured depends on the event type).</li>
                  <li>Signups appear instantly in your participants table in real-time</li>
                  <li>You can enable/disable signups anytime - disabled links show a "signups closed" message</li>
                  <li>Set signup limits - once reached, new visitors see the event is full</li>
                  <li>Share links anywhere: social media, email, QR codes, posters, etc.</li>
                </ul>
                <p className="mt-2"><strong>Manual Registration:</strong> You can also register participants directly 
                from the event management page if someone signs up in person or you prefer not to use public links.</p>
              </li>
              <li><strong>Metrics:</strong> Get metrics - see how many people signed up, turned up, played, what
                they do (solo, band, instruments etc),
                how long they spent on stage & more!
              </li>
            </ul>
          </div>

          {(user?.role === 'god' || user?.organisationUsers?.some(ou => ou.role === 'owner')) && (
            <div>
              <h4 className="font-medium mb-2">User Roles & Organization Management:</h4>
              <div className="text-sm text-slate-600 space-y-2">
                <p><strong>User Roles:</strong></p>
                <ul className="ml-4 list-disc space-y-1">
                  <li><strong>Owner:</strong> Can create/edit events, manage organization members, and control all event settings</li>
                  <li><strong>Operator:</strong> Can manage participants and stage during events, but cannot create events or manage users</li>
                  <li><strong>Member:</strong> Read-only access to view events they're part of</li>
                </ul>

                <p className="mt-3"><strong>Event Settings (Owner Only):</strong></p>
                <ul className="ml-4 list-disc space-y-1">
                  <li>Each organization can have multiple events and multiple users with different roles</li>
                  <li>Owners can create new users and assign their roles within the organization</li>
                  <li>Organizations keep events separate - users only see events for organizations they belong to</li>
                  <li>Toggle public signups on/off and set signup limits</li>
                  <li>Generate and share public signup links</li>
                  <li>Mark events as completed to archive them</li>
                </ul>
              </div>
            </div>
          )}

          <div>
            <h4 className="font-medium mb-2">Need More Help?</h4>
            <p className="text-sm text-slate-600">
              Contact your organization administrator or the app developer for additional support.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="w-full">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
